import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import SEO from "@/components/SEO";
import { Play, Pause, Music, ExternalLink, ArrowRight } from "lucide-react";

/**
 * /song — SEO landing page for the "Booty Breeze" anthem heard on TikTok.
 * People searching "booty breeze song" / "booty breeze tiktok song" land
 * here and can (a) play the full track, (b) shop the fresheners, (c) grab
 * a share link to loop the song back to TikTok.
 *
 * The full JSON-LD MusicRecording + WebPage schema gives Google + AI search
 * engines everything they need to attribute the song to this brand.
 */
const TRACK_URL = "https://customer-assets-m6fa6gv7.emergentagent.net/job_127be0a7-455f-4886-b271-789f0b3e698a/artifacts/76qrf13b_booty-breeze.mp3";
const TRACK_NAME = "Booty Breeze (Anthem)";
const ARTIST = "Booty Breeze";
const RELEASE_DATE = "2026-01-01";

export default function SongLanding() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    return () => {
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.muted = false; a.play(); }
    else a.pause();
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "MusicRecording",
      "name": TRACK_NAME,
      "byArtist": {
        "@type": "MusicGroup",
        "name": ARTIST,
        "url": "https://bootybreeze.com",
      },
      "duration": "PT2M30S",
      "inAlbum": {
        "@type": "MusicAlbum",
        "name": "Booty Breeze — The Anthem",
        "byArtist": { "@type": "MusicGroup", "name": ARTIST },
      },
      "datePublished": RELEASE_DATE,
      "url": "https://bootybreeze.com/song",
      "audio": TRACK_URL,
      "isFamilyFriendly": false,
      "keywords": [
        "booty breeze song", "booty breeze tiktok", "booty breeze anthem",
        "booty breeze mp3", "who sings booty breeze", "booty breeze theme song",
      ],
      "publisher": {
        "@type": "Organization",
        "name": "Booty Breeze",
        "url": "https://bootybreeze.com",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What's the Booty Breeze song from TikTok?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The Booty Breeze anthem is the official theme song for Booty Breeze, a small-batch adult (18+) car air freshener brand from the USA. You can play it in full at bootybreeze.com/song.",
          },
        },
        {
          "@type": "Question",
          "name": "Who made the Booty Breeze song?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Booty Breeze made and released the track as the brand's anthem — the same song you'll hear on the bottom-left boombox on every page of bootybreeze.com.",
          },
        },
        {
          "@type": "Question",
          "name": "Where can I download the Booty Breeze song?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The full track streams free at bootybreeze.com/song. Right-click the play button and choose 'Save audio as' to download a personal copy — please credit @bootybreeze if you use it in a video.",
          },
        },
        {
          "@type": "Question",
          "name": "What are Booty Breeze air fresheners?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Booty Breeze makes hand-poured, small-batch adult (18+) novelty car air fresheners — six unapologetic scents that last 3 to 4 weeks. Ships discreetly from the USA.",
          },
        },
      ],
    },
  ];

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-10 py-16" data-testid="song-landing">
      <SEO
        title="Booty Breeze Song (from TikTok) — Play the Full Anthem"
        description="Heard the Booty Breeze song on TikTok? Play the full anthem here, learn who made it, and grab the small-batch adult car air fresheners that inspired the track. Six hand-poured scents, ships discreet from the USA."
        path="/song"
        type="music.song"
        jsonLd={jsonLd}
      />

      <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-3">
        Heard us on TikTok?
      </div>
      <h1 className="font-display text-5xl sm:text-7xl text-white leading-[0.95] mb-6">
        The <span className="text-neon-pink">Booty Breeze</span> song.
      </h1>
      <p className="text-white/70 max-w-2xl text-lg leading-relaxed mb-10">
        You're in the right place. This is the anthem you've been humming — the
        theme song for our small-batch adult (18+) car air fresheners. Hit play,
        vibe out, and if you like what you smell, grab a freshener.
      </p>

      <div className="rounded-2xl border border-[#FF3D8B]/40 bg-[#0f0f0f] p-6 sm:p-8 mb-10" data-testid="song-player">
        <div className="flex items-center gap-5">
          <button
            onClick={toggle}
            data-testid="song-play-btn"
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#FF3D8B] text-black hover:bg-[#ff5ba0] transition-colors flex items-center justify-center shadow-lg shadow-[#FF3D8B]/40"
            aria-label={playing ? "Pause the Booty Breeze anthem" : "Play the Booty Breeze anthem"}
          >
            {playing ? <Pause size={36} /> : <Play size={36} className="translate-x-0.5" />}
          </button>
          <div className="flex-1">
            <div className="text-white/50 text-[10px] uppercase tracking-widest mb-1 flex items-center gap-2">
              <Music size={12} /> Now streaming
            </div>
            <div className="font-display text-2xl sm:text-3xl text-white leading-none">
              {TRACK_NAME}
            </div>
            <div className="text-white/60 text-sm mt-2">by {ARTIST}</div>
          </div>
        </div>
        <audio
          ref={audioRef}
          src={TRACK_URL}
          preload="auto"
          crossOrigin="anonymous"
          data-testid="song-audio-el"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-14">
        <Link
          to="/shop"
          data-testid="song-shop-cta"
          className="group rounded-xl border border-white/10 bg-[#0f0f0f] hover:border-[#FF3D8B] transition-colors p-6 flex items-center justify-between"
        >
          <div>
            <div className="text-white/50 text-[10px] uppercase tracking-widest mb-1">
              Grab the fresheners
            </div>
            <div className="font-display text-xl text-white group-hover:text-[#FF3D8B] transition-colors">
              Shop the six scents →
            </div>
          </div>
          <ArrowRight className="text-[#FF3D8B]" size={22} />
        </Link>
        <a
          href="https://www.tiktok.com/search?q=booty+breeze"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="song-tiktok-cta"
          className="group rounded-xl border border-white/10 bg-[#0f0f0f] hover:border-[#A855F7] transition-colors p-6 flex items-center justify-between"
        >
          <div>
            <div className="text-white/50 text-[10px] uppercase tracking-widest mb-1">
              Use it in your video
            </div>
            <div className="font-display text-xl text-white group-hover:text-[#A855F7] transition-colors">
              Find us on TikTok
            </div>
          </div>
          <ExternalLink className="text-[#A855F7]" size={22} />
        </a>
      </div>

      <section className="mb-10" data-testid="song-faq">
        <h2 className="font-display text-3xl text-white mb-6">The song, explained</h2>
        <dl className="space-y-6 text-white/80">
          <div>
            <dt className="text-[#FF3D8B] font-semibold mb-1">What is the Booty Breeze song from TikTok?</dt>
            <dd className="text-white/70">
              The Booty Breeze anthem is the official theme song for Booty Breeze — a small-batch
              adult (18+) car air freshener brand from the USA. Play it in full above.
            </dd>
          </div>
          <div>
            <dt className="text-[#FF3D8B] font-semibold mb-1">Who made it?</dt>
            <dd className="text-white/70">
              We did. Booty Breeze wrote and released it as the brand's anthem — the same track
              you'll hear on the bottom-left boombox on every page of the site.
            </dd>
          </div>
          <div>
            <dt className="text-[#FF3D8B] font-semibold mb-1">Can I use it in my TikTok?</dt>
            <dd className="text-white/70">
              Yes — please tag <span className="text-white">@bootybreeze</span> so we can share it.
              Even better, feature a freshener in the video and use code <span className="text-[#FF3D8B] font-mono">TIKTOK10</span> for
              10% off your first order.
            </dd>
          </div>
          <div>
            <dt className="text-[#FF3D8B] font-semibold mb-1">Where do I download it?</dt>
            <dd className="text-white/70">
              Right-click the play button above and choose <em>Save audio as…</em> — please credit us if you post it.
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
}
